import { useSelector } from "react-redux";
import { RootState } from "../../services/redux/store";
import React from "react";
import { Box, Grid, Typography } from "@mui/material";
import DetailRow from "../../pages/common-features/DetailRow";
import { camelToTitle } from "../ui/StepperDyanmicForm/helper";

interface AddressDetailsTabProps {
  addressDetails: any;
  isMobile: boolean;
}

const AddressDetailsTab: React.FC<AddressDetailsTabProps> = ({
  addressDetails,
  isMobile,
}) => {
  const { fetchUserApplicationData } = useSelector(
    (state: RootState) => state.stepper
  );

  // Helper to map address fields from the vertical step to the required format
  function mapAddressFields(fields: any[], prefix: string) {
    if (!fields) return [];
    return fields
      .filter((fieldObj: any) => Object.keys(fieldObj)[0].startsWith(prefix))
      .map((fieldObj: any) => {
        const name = Object.keys(fieldObj)[0];
        const value = fieldObj[name];
        // Remove prefix and capitalize each word, add space before trailing numbers
        let label = name.replace(new RegExp(`^${prefix}`), "");
        label = label.replace(/([A-Z])/g, " $1").trim();
        label = label.replace(/(\D)(\d+)$/, "$1 $2");
        label = label.replace(/^./, (str) => str.toUpperCase());
        label = label
          .split(" ")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ");
        return {
          label,
          name,
          value,
        };
      });
  }

  let addressDetailsToShow = addressDetails;
  if (
    fetchUserApplicationData &&
    fetchUserApplicationData?.partnerStartedJourney &&
    addressDetailsToShow?.length === 0     
  ) {
    addressDetailsToShow = addressDetails || {}
  }

  if (
    fetchUserApplicationData &&
    !fetchUserApplicationData?.partnerStartedJourney
  ) {
    // Find the 'Applicant & Loan Details' horizontal step
    const applicantLoanStep = fetchUserApplicationData?.horizontalSteps?.find(
      (h: any) => h.horizontalStepName === "Applicant & Loan Details"
    );
    // Find the 'Address Details' vertical step
    const addressDetailsStep = applicantLoanStep?.verticalSteps?.find(
      (v: any) => v.verticalStepName === "Address Details"
    );
    const fields = addressDetailsStep?.fields || [];
    if (addressDetailsStep) {
      addressDetailsToShow = {
        currentAddress: mapAddressFields(fields, "current"),
        permanentAddress: mapAddressFields(fields, "permanent"),
      };
    }
  }

  if (!addressDetailsToShow) {
    return (
      <Grid item xs={12}>
        <Typography variant="body2" color="textSecondary">
          No details available.
        </Typography>
      </Grid>
    );
  }

  const renderSectionHeading = (title: string, marginTop: number) => (
    <Grid item xs={12} sx={{ mt: marginTop, mb: 1 }}>
      <Box
        sx={{
          backgroundColor: "#edf6f6",
          borderLeft: "3px solid #00b3b5",
          borderRadius: "8px",
          px: 2,
          py: 1.25,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontSize: "15px", fontWeight: 600, color: "#6f6b7d" }}
        >
          {title}
        </Typography>
      </Box>
    </Grid>
  );

  return (
    <>
      {renderSectionHeading("Current Address", 2)}

      {addressDetailsToShow?.currentAddress?.length > 0 ? (
        addressDetailsToShow?.currentAddress?.map(
          (field: any, subIndex: number) => {
            const skipNames = ["currentaddresstype", "currentsameaspermanent"];
            if (skipNames.includes(field.name.toLowerCase())) return null;
            return (
              <Grid item xs={12} sm={6} md={3} key={`current-${subIndex}`}>
                <DetailRow
                  item={{
                    label: field.label,
                    value: field.value,
                    isVerified: false,
                  }}
                />
              </Grid>
            );
          }
        )
      ) : (
        <Grid item xs={12}>
          <Typography variant="body2" color="textSecondary">
            No details available.
          </Typography>
        </Grid>
      )}

      {renderSectionHeading("Permanent Address", isMobile ? 5 : 4)}
      {addressDetailsToShow?.permanentAddress?.length > 0 ? (
        addressDetailsToShow?.permanentAddress?.map(
          (field: any, subIndex: number) => {
            const skipNames = ["permanentaddresstype"];
            if (skipNames.includes(field.name.toLowerCase())) return null;
            return (
              <Grid item xs={12} sm={6} md={3} key={`permanent-${subIndex}`}>
                <DetailRow
                  item={{
                    label: field.label,
                    value: field.value,
                    isVerified: false,
                  }}
                />
              </Grid>
            );
          }
        )
      ) : (
        <Grid item xs={12}>
          <Typography variant="body2" color="textSecondary">
            No details available.
          </Typography>
        </Grid>
      )}
    </>
  );
};

export default AddressDetailsTab;
